import { useProfileContext } from "@hooks/context/useProfileContext";
import { ClickAwayListener, Popper, Skeleton } from "@mui/material";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import Icon from "@ui/atoms/Icon/Icon";
import * as RouteHelper from "@utils/paths";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ProjectDetailLineItem,
  ProjectLineItem
} from "catchment-common/interfaces/project";

import { SidebarLayoutType } from "../const";
import {
  FoldingChevron,
  FoldingText,
  SIDEBAR_RAIL_TILE
} from "../MainSidebar.components";
import {
  ScopeCaption,
  ScopeCard,
  ScopeDivider,
  ScopeFooterLink,
  ScopeName,
  ScopePanel,
  ScopeRow,
  ScopeSub,
  ScopeTile
} from "./ScopeSwitcher.components";
import {
  duplicateProjectNames,
  initialsOf,
  projectDisplayName,
  projectKey,
  projectLocationLabel,
  sortProjectsByName
} from "./ScopeSwitcher.helpers";

/**
 * Sized, with the card's left padding, so the tile's centre lands on the rail
 * tile's — 30px from the nav's edge, the same place every menu icon sits.
 * See `COLLAPSED_WIDTH`.
 */
const CARD_TILE = 26;
const ROW_TILE = 24;
const MENU_ID = "sidebar-projects-menu";

export interface ScopeSwitcherProps {
  layoutType: SidebarLayoutType;
  isDrawOpen: boolean;
  loading: boolean;
  projects: ProjectDetailLineItem[];
  currentProject?: ProjectLineItem;
  currentContractorPackageId?: string;
  switchProject: (projectId: string, contractorPackageId: string) => void;
  /** Told when the panel opens or closes, so the nav can dim behind it. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * The one place to move between the organisation and its projects. Replaces
 * the org sidebar's static heading and the project sidebar's projects dropdown
 * with a single card (a tile in the rail) that opens a panel listing both.
 *
 * The organisation is a row like any project — selecting it lands on the org
 * dashboard, the same way selecting a project lands on that project's — so the
 * two scopes read as siblings rather than as two different navs.
 */
export const ScopeSwitcher = ({
  currentContractorPackageId,
  currentProject,
  isDrawOpen,
  layoutType,
  loading,
  onOpenChange,
  projects,
  switchProject
}: ScopeSwitcherProps) => {
  const navigate = useNavigate();
  const { userOrganisation } = useProfileContext();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const isOrganisationScope = layoutType === SidebarLayoutType.ORGANISATION;
  const organisationName = userOrganisation?.displayName ?? "";

  const sortedProjects = useMemo(
    () => sortProjectsByName(projects),
    [projects]
  );
  const duplicates = useMemo(() => duplicateProjectNames(projects), [projects]);

  const currentKey =
    currentProject && currentContractorPackageId
      ? projectKey({
          contractorPackageId: currentContractorPackageId,
          id: currentProject.id
        })
      : "";
  const currentDetail = sortedProjects.find(
    (project) => projectKey(project) === currentKey
  );

  // What the card shows. In project scope it is the current project — from the
  // list when it is there (so a duplicate name gets its suffix), else the bare
  // project so the card is never blank while the list loads.
  const scopeName = isOrganisationScope
    ? organisationName
    : currentDetail
      ? projectDisplayName(currentDetail, duplicates)
      : (currentProject?.projectName ?? "");
  // Plain functions: neither is handed to a memoised child, and `toggle` reads
  // `open` so it was rebuilt every render anyway.
  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  const close = () => setOpenState(false);
  const toggle = () => setOpenState(!open);

  const selectOrganisation = () => {
    close();
    if (!isOrganisationScope) navigate(RouteHelper.organisationDashboard);
  };

  const selectProject = (project: ProjectDetailLineItem) => {
    close();
    if (projectKey(project) === currentKey) return;
    switchProject(project.id, project.contractorPackageId);
  };

  const viewAllProjects = () => {
    close();
    navigate(RouteHelper.projects);
  };

  // The open card is full-width and 48 tall; folded it is the square rail tile.
  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={isDrawOpen ? undefined : SIDEBAR_RAIL_TILE}
        height={isDrawOpen ? 48 : SIDEBAR_RAIL_TILE}
        sx={{ borderRadius: "10px", flex: "none" }}
      />
    );
  }

  // One control in both states: it folds down to the tile rather than being
  // swapped for a different element halfway through the animation.
  const trigger = (
    <BaseTooltip title={isDrawOpen || open ? "" : scopeName} placement="right">
      <ScopeCard
        ref={anchorRef}
        open={open}
        expanded={isDrawOpen}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? MENU_ID : undefined}
        aria-label={isDrawOpen ? undefined : scopeName}
        data-test-id="sidebar-scope-switcher"
      >
        <ScopeTile
          size={isDrawOpen ? CARD_TILE : SIDEBAR_RAIL_TILE}
          tone="tint"
        >
          {initialsOf(scopeName)}
        </ScopeTile>
        {/* The name alone at rest. The sub-line — "Organisation Home", a
            project's location — says which SCOPE this is, which is a question
            you only ask while choosing one, so it belongs in the panel below
            (where every row carries it) and not on the card. Dropping it makes
            the card a single line, which is what lets its padding come in far
            enough for the tile to sit where the rail tile will. */}
        <FoldingText visible={isDrawOpen}>
          <ScopeName data-test-id="sidebar-selected-project" title={scopeName}>
            {scopeName}
          </ScopeName>
        </FoldingText>
        <FoldingChevron visible={isDrawOpen}>
          <Icon name={open ? "chevron-up" : "chevron-down"} size="xs" />
        </FoldingChevron>
      </ScopeCard>
    </BaseTooltip>
  );

  return (
    <>
      {trigger}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        // Below the card when expanded; beside the rail when collapsed.
        placement={isDrawOpen ? "bottom-start" : "right-start"}
        modifiers={[
          { name: "offset", options: { offset: isDrawOpen ? [0, 6] : [0, 8] } }
        ]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={close}>
          <ScopePanel id={MENU_ID} role="menu" elevation={0}>
            <ScopeCaption>Organisation</ScopeCaption>
            <ScopeRow
              role="menuitem"
              selected={isOrganisationScope}
              onClick={selectOrganisation}
              data-test-id="sidebar-organisation-option"
            >
              <ScopeTile
                size={ROW_TILE}
                tone={isOrganisationScope ? "card" : "hover"}
              >
                {initialsOf(organisationName)}
              </ScopeTile>
              <FoldingText>
                <ScopeName compact>{organisationName}</ScopeName>
                <ScopeSub>Organisation Home</ScopeSub>
              </FoldingText>
              {isOrganisationScope ? <Icon name="check" size="xs" /> : null}
            </ScopeRow>

            <ScopeCaption style={{ paddingTop: 10 }}>
              Projects · {sortedProjects.length}
            </ScopeCaption>
            {sortedProjects.map((project) => {
              const key = projectKey(project);
              const selected = !isOrganisationScope && key === currentKey;
              return (
                <ScopeRow
                  key={key}
                  role="menuitem"
                  selected={selected}
                  onClick={() => selectProject(project)}
                  data-test-id={`sidebar-project-option-${key}`}
                >
                  <ScopeTile size={ROW_TILE} tone={selected ? "card" : "hover"}>
                    {initialsOf(project.projectName)}
                  </ScopeTile>
                  <FoldingText>
                    <ScopeName compact>
                      {projectDisplayName(project, duplicates)}
                    </ScopeName>
                    <ScopeSub>
                      {projectLocationLabel(project) ||
                        project.organisationName}
                    </ScopeSub>
                  </FoldingText>
                  {selected ? <Icon name="check" size="xs" /> : null}
                </ScopeRow>
              );
            })}

            <ScopeDivider />
            <ScopeFooterLink onClick={viewAllProjects} disableRipple>
              View all projects
              <Icon name="chevron-right" size="xs" />
            </ScopeFooterLink>
          </ScopePanel>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
